import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { usePrivateStore } from '@/store/usePrivateStore';
import { PeriodDTO, YearStage } from '@/types/period';
import useOrganizationCache from '@/hooks/useOrganizationIndexedDB';
import { getCurrentQuarterRangeString } from '@/helper';
import { mapAndSortPeriods } from '@/utils/periodUtils';

const { Option, OptGroup } = Select;

interface SelectedValue {
  idGiaiDoan: string;
  year: number;
  namTaiChinh?: boolean;
  startDate?: string | null;
  endDate?: string | null;
}

interface YearSelectorProps {
  onChange: (value: SelectedValue) => void;
  data?: YearStage[];
  yearSelected?: number;
  isShowYear?: boolean;
  noChangeYear?: boolean;
  getPopupContainer?: () => HTMLElement;
  /** Năm không được chọn (vd. năm đích đang copy dữ liệu). */
  disabledYears?: number[];
}

export const YearSelectorBSC: React.FC<YearSelectorProps> = ({ onChange, data, yearSelected, isShowYear = true, noChangeYear = false, getPopupContainer, disabledYears = [] }) => {
  const { t } = useTranslation();
  const { giaiDoan: { id: idGiaiDoan, year: yearGiaiDoan }, setGiaiDoan } = usePrivateStore();
  const {dateChoiceDefault, setDateChoiceDefault } = usePrivateStore();
  
  const [localSelectedYear, setLocalSelectedYear] = useState<number | null>(yearSelected || null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [periodData, setPeriodData] = useState<PeriodDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const [selectedGiaiDoan, setSelectedGiaiDoan] = useState<string | null>(null);
  const [isNamTaiChinh, setIsNamTaiChinh] = useState<boolean>(false);
  const giaiDoanOptions = useMemo(() => {
    return periodData.map((period) => ({
      value: period.id.toString(),
      label: period.ten,
    }));
  }, [periodData]);
  const { getPeriods } = useOrganizationCache();

  // Load data từ getPeriods
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        setLoading(true);
        const periods = await getPeriods();
        
        if (!periods?.length) {
          setPeriodData([]);
          return;
        }

        const sortedPeriods = mapAndSortPeriods(periods);
        setPeriodData(sortedPeriods);
      } catch (error) {
        console.error('Error loading period data:', error);
        setPeriodData([]);
      } finally {
        setLoading(false);
      }
    };
    
    if ((!data || data.length === 0) && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchPeriods();
    }
  }, [data, getPeriods]);

  // Convert về StageYear format
  const convertedData = useMemo(() => {
    if (data && data.length > 0) {
      return data;
    }

    return periodData.map((period) => {
      const years: string[] = [];
      if (period.periodDetails && period.periodDetails.length > 0) {
        period.periodDetails.forEach((detail) => {
          years.push(detail.year.toString());
        });
      }
      if(period.namTaiChinh) {
        setIsNamTaiChinh(true);
      }
      return {
        idGiaiDoan: period.id,
        label: period.ten,
        years: years.sort(),
        namTaiChinh: period.namTaiChinh || false,
        startDate: period.batDau || null,
        endDate: period.ketThuc || null,
      };
    });
  }, [data, periodData]);

  const disabledYearSet = useMemo(() => new Set(disabledYears), [disabledYears]);

  const options = useMemo(() => {
    return convertedData.map((stage) => {
      const labelText = stage.namTaiChinh === true
        ? `${stage.label} (${t('bsc.label.namtaichinh', 'Năm tài chính').toLowerCase()})`
        : stage.label;
      const customLabel = <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{labelText}</span>;

      return (
        <OptGroup key={stage.idGiaiDoan} label={customLabel}>
          {stage.years.map((year) => (
            <Option
              key={`${stage.idGiaiDoan}-${year}`}
              value={year}
              disabled={disabledYearSet.has(parseInt(String(year), 10))}
            >
              {year}
            </Option>
          ))}
        </OptGroup>
      );
    });
  }, [convertedData, disabledYearSet, t]);
  
  const handleChange = (value: string) => {
    const year = parseInt(String(value), 10) || 0;
    if (disabledYearSet.has(year)) {
      return;
    }
    const selectedStage = convertedData.find((stage) => stage.years.includes(String(value)));
    if (selectedStage) {
      const idGiaiDoan = selectedStage.idGiaiDoan.toString();
      
      setLocalSelectedYear(year);
      setSelectedStageId(idGiaiDoan);
      // [DISABLED] Không commit setGiaiDoan vào global store ngay khi người dùng chọn năm.
      // Lý do: YearSelectorBSC được dùng bên trong DatePickerBSC — người dùng còn có thể
      // tiếp tục chọn tháng/quý trước khi bấm "Áp dụng". Việc gọi setGiaiDoan ở đây làm
      // state toàn cục thay đổi sớm, không đúng flow. setGiaiDoan sẽ được gọi trong
      // handleSelect() của DatePickerBSC khi người dùng bấm "Áp dụng".
      // if (!noChangeYear) {
      //   setGiaiDoan(idGiaiDoan, year, reporting?.dateChoice ?? null, selectedStage.namTaiChinh || false, selectedStage.startDate || null, selectedStage.endDate || null);
      // }
      onChange({ idGiaiDoan, year, namTaiChinh: selectedStage.namTaiChinh || false, startDate: selectedStage.startDate || null, endDate: selectedStage.endDate || null });
    }
  };

  // Đồng bộ khi có dữ liệu store
  useEffect(() => {
    if (idGiaiDoan !== selectedStageId) {
      setSelectedStageId(idGiaiDoan);
    }
    if (idGiaiDoan && !selectedStageId) {
      setSelectedStageId(idGiaiDoan);
    }
    if (!noChangeYear) {
      if (yearGiaiDoan !== localSelectedYear) {
        setLocalSelectedYear(yearGiaiDoan);
      }
      if (yearGiaiDoan && !localSelectedYear) {
        setLocalSelectedYear(yearGiaiDoan);
      }
    }
  }, [idGiaiDoan, yearGiaiDoan, selectedStageId, localSelectedYear, noChangeYear]);

  // ✅ Khởi tạo mặc định: ưu tiên năm hiện tại, fallback về năm mới nhất có trong data
  useEffect(() => {
    if (localSelectedYear && convertedData.length > 0 && idGiaiDoan === null) {
      let selectedYear = localSelectedYear;
      let foundStage = convertedData.find((stage) => stage.years.includes(String(selectedYear)));
      
      if (!foundStage) {
        const allYears = convertedData.flatMap((stage) => stage.years.map((y) => parseInt(y)));
        const currentYear = selectedYear;
        
        // Tìm năm gần nhất với năm hiện tại
        // Nếu có 2 năm cách đều (ví dụ 2025 và 2027 khi hiện tại là 2026), chọn năm nhỏ hơn (2025)
        const nearestYear = allYears.reduce((closest, year) => {
          const distanceToClosest = Math.abs(closest - currentYear);
          const distanceToYear = Math.abs(year - currentYear);
          
          // Nếu khoảng cách bằng nhau, chọn năm nhỏ hơn
          if (distanceToYear === distanceToClosest) {
            return year < closest ? year : closest;
          }
          
          // Nếu khoảng cách nhỏ hơn, chọn năm này
          return distanceToYear < distanceToClosest ? year : closest;
        }, allYears[0]); // Khởi tạo với năm đầu tiên
        
        selectedYear = nearestYear;
        foundStage = convertedData.find((stage) => stage.years.includes(String(selectedYear)));
      }
      
      if (foundStage) {
        const idGiaiDoan = foundStage.idGiaiDoan.toString();
        const dateRange = getCurrentQuarterRangeString(
          selectedYear,
          foundStage.namTaiChinh || false,
          foundStage.startDate ? new Date(foundStage.startDate).getMonth() + 1 : 0
        );

        setLocalSelectedYear(selectedYear);
        setSelectedStageId(idGiaiDoan);
        setGiaiDoan(idGiaiDoan, selectedYear, dateRange, foundStage.namTaiChinh || false, foundStage.startDate || null, foundStage.endDate || null);
        onChange({ idGiaiDoan, year: selectedYear || 0, namTaiChinh: foundStage.namTaiChinh || false, startDate: foundStage.startDate || null, endDate: foundStage.endDate || null });
        if (dateChoiceDefault === null) {
          setDateChoiceDefault(dateRange);
        }
      }
    }
  }, [convertedData, localSelectedYear, idGiaiDoan, setGiaiDoan, onChange, data, periodData, dateChoiceDefault, setDateChoiceDefault]);

  // Đồng bộ khi prop yearSelected thay đổi
  useEffect(() => {
    if (yearSelected) {
      for (const stage of convertedData) {
        if (stage.years.includes(String(yearSelected))) {
          setLocalSelectedYear(Number(yearSelected));
          setSelectedStageId(stage.idGiaiDoan.toString());
          break;
        }
      }
    }
  }, [yearSelected, convertedData]);

  const yearPlaceholder = loading
    ? t('bsc.msg.dangtai', 'Đang tải...')
    : `${t('bsc.btn.chon', 'Chọn')} ${t('bsc.label.nam', 'Năm').toLowerCase()}`;

  const stagePlaceholder = loading
    ? t('bsc.msg.dangtai', 'Đang tải...')
    : `${t('bsc.btn.chon', 'Chọn')} ${t('bsc.label.giaidoan', 'Giai đoạn').toLowerCase()}`;

  const yearSelectValue = useMemo(() => {
    if (loading || !convertedData.length || localSelectedYear == null) {
      return null;
    }
    const yearStr = String(localSelectedYear);
    return convertedData.some((stage) => stage.years.includes(yearStr)) ? yearStr : null;
  }, [loading, convertedData, localSelectedYear]);

  const stageSelectValue = useMemo(() => {
    if (loading || !giaiDoanOptions.length) {
      return null;
    }
    const stageId = selectedStageId || selectedGiaiDoan;
    if (!stageId) {
      return null;
    }
    return giaiDoanOptions.some((opt) => opt.value === stageId) ? stageId : null;
  }, [loading, giaiDoanOptions, selectedStageId, selectedGiaiDoan]);

  return (
    <>
      {isShowYear && (
        <Select
          className="w-full custom-select z-32"
          placeholder={yearPlaceholder}
          onChange={handleChange}
          value={yearSelectValue}
          getPopupContainer={(triggerNode: HTMLElement) => getPopupContainer ? getPopupContainer() : triggerNode.parentElement!}
          loading={loading}
          disabled={loading}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: isNamTaiChinh ? 220 : 130 }}
          variant={'borderless'}
          size='small'
        >
          {options}
        </Select>
      )}
      {!isShowYear && (
        <Select
          className="custom-select min-w-[112px] max-w-[220px] [&_.ant-select-selection-item]:truncate"
          style={{ width: 'fit-content' }}
          popupMatchSelectWidth={false}
          value={stageSelectValue}
          getPopupContainer={() => document.body}
          onChange={(value) => {
            setSelectedGiaiDoan(value);
            const foundPeriod = periodData.find((period) => period.id.toString() === value);
            if (foundPeriod) {
              let selectedYear = localSelectedYear;
              if (!foundPeriod.periodDetails?.some((d) => d.year === selectedYear)) {
                const yearCurrent = new Date().getFullYear();

                if (foundPeriod.periodDetails?.some((d) => d.year === yearCurrent)) {
                  selectedYear = yearCurrent;
                } else {
                  // Chọn năm gần nhất với năm hiện tại (nhưng phải <= năm hiện tại)
                  const availableYears = foundPeriod.periodDetails!
                    .map((d) => d.year)
                    .filter((year) => year <= yearCurrent)
                    .sort((a, b) => b - a); // Sắp xếp giảm dần để lấy năm lớn nhất
                  
                  selectedYear = availableYears.length > 0 
                    ? availableYears[0] // Năm gần nhất và <= năm hiện tại
                    : Math.min(...foundPeriod.periodDetails!.map((d) => d.year)); // Fallback: nếu không có năm nào <= năm hiện tại, lấy năm nhỏ nhất
                }
              }
              setSelectedStageId(value);
              setLocalSelectedYear(selectedYear);
              if (!noChangeYear) {
                setGiaiDoan(value, selectedYear, null, foundPeriod.namTaiChinh || false, foundPeriod.batDau || null, foundPeriod.ketThuc || null);
              }
              onChange({ idGiaiDoan: value, year: selectedYear || 0, namTaiChinh: foundPeriod.namTaiChinh || false, startDate: foundPeriod.batDau || null, endDate: foundPeriod.ketThuc || null });
            }
          }}
          options={giaiDoanOptions}
          placeholder={stagePlaceholder}
          loading={loading}
          disabled={loading}
          notFoundContent={`${t('bsc.msg.khongcodulieu', 'Không có dữ liệu!')} ${t('bsc.label.giaidoan', 'Giai đoạn').toLowerCase()}`}
          variant={'borderless'}
          size='small'
        />
      )}

      <style>
        {`
          .custom-select .ant-select-selector {
            border: none !important;
          }
          .custom-select .ant-select-arrow {
            right: 5px;
            font-size: 10px;
          }
         .custom-select .ant-select-selector .ant-select-selection-item {
            font-size: 12px !important;
            font-weight: 700 !important;
            border-radius: 8px !important;
            background-color: #919EAB29 !important;
            padding-left: 10px !important;          
          }
        `}
      </style>
    </>
  );
};
